import { Component, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthValidationError, ChangePasswordFailure, ChangePasswordRequest, ChangePasswordSuccess } from '../../../../../store/auth/auth.actions';
import { take } from 'rxjs/internal/operators/take';
import { ValidationErrorComponent } from '../../../../../components/core/validation-error/validation-error.component';

@Component({
  selector: 'app-change-password-modal',
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, ValidationErrorComponent],
  templateUrl: './change-password-modal.component.html',
  styleUrl: './change-password-modal.component.scss'
})
export class ChangePasswordModalComponent {
  private readonly dialogRef = inject(MatDialogRef<ChangePasswordModalComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  validationError = signal<string[]>([]);
  protected readonly showOldPassword = signal(false);
  protected readonly showNewPassword = signal(false);
  protected readonly showConfirmPassword = signal(false);

  protected readonly form = this.fb.nonNullable.group(
    {
      old_password: ['', [Validators.required]],
      new_password: ['', [Validators.required, Validators.minLength(8)]],
      confirm_password: ['', [Validators.required]],
    },
    { validators: [this.passwordsMatchValidator] }
  );

  protected close(): void {
    this.dialogRef.close();
  }

  protected save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.store.dispatch(ChangePasswordRequest({ payload: { old_password: this.form.controls.old_password.value!, new_password: this.form.controls.new_password.value! } }));
    
    this.actions$.pipe(ofType(ChangePasswordSuccess), take(1)).subscribe(() => {
        this.dialogRef.close(this.form.getRawValue());
    });

    this.actions$.pipe(ofType(AuthValidationError), take(1)).subscribe(({ validationError }) => {
      this.validationError.set(validationError);
    });

    this.actions$.pipe(ofType(ChangePasswordFailure), take(1)).subscribe(({ error }) => {
      this.validationError.set([error]);
    });

  }

  protected hasControlError(controlName: 'old_password' | 'new_password' | 'confirm_password', errorName: string): boolean {
    const control = this.form.controls[controlName];
    return control.touched && control.hasError(errorName);
  }

  protected hasPasswordMismatch(): boolean {
    return this.hasControlError('confirm_password', 'passwordMismatch');
  }

  protected togglePasswordVisibility(field: 'old' | 'new' | 'confirm'): void {
    if (field === 'old') {
      this.showOldPassword.update((value) => !value);
      return;
    }

    if (field === 'new') {
      this.showNewPassword.update((value) => !value);
      return;
    }

    this.showConfirmPassword.update((value) => !value);
  }

  private passwordsMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('new_password')?.value;
    const confirmPassword = control.get('confirm_password')?.value;
    const confirmControl = control.get('confirm_password');

    if (!confirmControl) {
      return null;
    }

    if (!newPassword || !confirmPassword) {
      const currentErrors = confirmControl.errors;
      if (currentErrors?.['passwordMismatch']) {
        const { passwordMismatch, ...remaining } = currentErrors;
        confirmControl.setErrors(Object.keys(remaining).length ? remaining : null);
      }
      return null;
    }

    if (newPassword !== confirmPassword) {
      const currentErrors = confirmControl.errors ?? {};
      if (!currentErrors['passwordMismatch']) {
        confirmControl.setErrors({ ...currentErrors, passwordMismatch: true });
      }
      return { passwordMismatch: true };
    }

    const currentErrors = confirmControl.errors;
    if (currentErrors?.['passwordMismatch']) {
      const { passwordMismatch, ...remaining } = currentErrors;
      confirmControl.setErrors(Object.keys(remaining).length ? remaining : null);
    }

    return null;
  }
}
